import {
  ContextSelector,
  createContext,
  useContextSelector,
} from '@fluentui/react-context-selector';
import { NextRouter, useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { Dispatch, useCallback } from 'react';

import { AgentState } from '../components/_organisms/DesignerTabPane/AgentTab/AgentHooks';
import { SavedRecentPrompt } from '../components/dynamicPages/CreatePage/CreatePage';
import { isToolCall } from '../components/dynamicPages/CreatePage/intent-refinement/use-agent-history-messages';
import { Page, UploadedFile } from '../components/dynamicPages/CreatePage/types';
import { LLMMessageDTO } from '../services/backendService/backendService';
import { Standards } from '../services/backendService/types';
import { findlessonIntent, SubjectOption } from '../utils/createLessonPageUtils';
import { ErrorDTO } from './useDesignerUIState';
import { useReducerWithMiddlewareDynamicLesson } from './useReducerWithMiddlewaresDynamicLesson';

type SavedDynamicLessonState = Partial<DynamicLessonState>;
export const LS_KEY_DYNAMIC_LESSON_STATE = '@curipod_v1_createWithAI-DynamicLessonState';
export const saveLocalDynamicLessonState = (state: SavedDynamicLessonState) => {
  try {
    const existing = loadDynamicLessonState() || {};
    const merged = {
      ...existing,
      ...state,
    };
    localStorage.setItem(LS_KEY_DYNAMIC_LESSON_STATE, JSON.stringify(merged));
  } catch (err) {
    console.warn('Failed to save dynamic lesson state', err);
  }
};

export const loadDynamicLessonState = (): SavedDynamicLessonState | null => {
  try {
    const item = localStorage.getItem(LS_KEY_DYNAMIC_LESSON_STATE);
    return item ? JSON.parse(item) : null;
  } catch (err) {
    console.warn('Failed to load dynamic lesson state', err);
    return null;
  }
};

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface DynamicLessonState {
  //*General
  mainLessonCategory?:
    | 'full_lesson'
    | 'test_prep'
    | 'writing_skills'
    | 'upload'
    | 'pick_theme'; // eg. 'full_lesson', 'test_prep', etc.
  grade: string;
  languages: string[];
  selectedStandard?: Standards;
  prompt?: string;

  //*Full Lesson
  subject?: string;
  lessonIntent?: SubjectOption; // called "lessonType" in some places
  selectedPage: Page;
  additionalCriteria?: string; //advanced prompting only

  //*Full Lesson: Teacher inputs to build prompt
  needSupportWith?: string; // typed input
  subjectFocus?: string; // dropdown
  targetSkill?: string; // dropdown
  addedOwnTargetSkill?: string; // typed input
  lessonTheme?: string; // typed input
  chapter?: string; // typed input
  novel?: string; // typed input

  //*Curify
  uploadedFile?: UploadedFile;

  //*Writing skills / Daily routines
  generatorFilterCategory?: string; //used to filter generators

  // State for the learning objective agent
  agent: {
    agentHistory: LLMMessageDTO[];

    // General agent state
    isStreaming: boolean;
    state: 'thinking' | 'generating' | null;
    finishReason: 'completed' | 'cancelled' | 'error' | null;
    error: ErrorDTO | null;
    tokens: string[];
    currentContent: string;
    isComplete: boolean;
    isConnected: boolean;

    // Learning objective specific state
    isObjectiveReady: boolean;
    promptVariables: PromptVariables | null;
    llmThreadId: string | null;
  };

  presentationId?: string; // used for flexible lesson before we redirect the user to the designer
}

export interface PromptVariables {
  learningObjective: string;
  grade: string;
  subject?: string;
  subjectCategory?: 'ela' | 'math' | 'science' | 'socialStudies' | 'other';
  languages?: string[];
  lessonCategory?:
    | 'teachSomethingNew'
    | 'reviewOrReteach'
    | 'practiceASkill'
    | 'readAloud'
    | 'closeReading'
    | 'other';
  standardAlignment?: string;
  attachments?: { fileUploadId: string }[];
}

export type DynamicLessonAction =
  | { type: 'RESET_ALL' }
  | { type: 'RESET_SUBJECTS' }
  | { type: 'RESET_LESSON_INTENT' }
  | { type: 'GO_BACK'; payload: number }
  | { type: 'SET_PAGE'; payload: number }
  | {
      type: 'SET_MAIN_LESSON_CATEGORY';
      payload: 'full_lesson' | 'test_prep' | 'writing_skills' | 'upload' | 'pick_theme';
    }
  | { type: 'SET_SUBJECT'; payload: { subject: string; hasNewBlockAccess: boolean } }
  | { type: 'SET_GENERATOR_FILTER_CATEGORY'; payload: string }
  | { type: 'SET_LESSON_INTENT'; payload: SubjectOption; isDesktop?: boolean }
  | { type: 'SET_UPLOADED_FILE'; payload: UploadedFile | undefined }
  | { type: 'SET_GRADE'; payload: string }
  | { type: 'SET_ADVANCED_PROMPT' }
  | { type: 'SET_LANGUAGE'; payload: string }
  | { type: 'SET_TARGET_SKILL'; payload: string }
  | { type: 'SET_SUBJECT_FOCUS'; payload: string }
  | { type: 'SET_ADDED_OWN_TARGET_SKILL'; payload: string | undefined }
  | { type: 'SET_NOVEL'; payload: string | undefined }
  | { type: 'SET_CHAPTER'; payload: string | undefined }
  | { type: 'SET_NEEDS_SUPPORT'; payload: string | undefined }
  | { type: 'SET_LESSON_THEME'; payload: string | undefined }
  | { type: 'SET_ADDITIONAL_CRITERIA'; payload: string | undefined }
  | {
      type: 'SET_SELECTED_STANDARD';
      payload: { selectedStandard?: Standards; subject: string; grade: string };
    }
  | { type: 'CLEAR_SELECTED_STANDARD' }
  | { type: 'ON_PROMPT_CHANGE'; payload: string | undefined }
  | { type: 'HANDLE_RECENT_PROMPT_SELECTED'; payload: SavedRecentPrompt }

  // AGENT ACTIONS
  | { type: 'SET_AGENT_HISTORY'; payload: LLMMessageDTO[] }
  | { type: 'SET_AGENT_HISTORY_FROM_SERVER'; payload: LLMMessageDTO[] }
  | { type: 'SET_AGENT_STREAM_STARTED' }
  | { type: 'RESET_AGENT' }
  | { type: 'SET_AGENT_STREAM_ERROR'; payload: ErrorDTO | null }
  | { type: 'CLEAR_AGENT_ERROR' }
  | {
      type: 'SET_AGENT_STREAM_ON_TOKEN_RECEIVED';
      payload: {
        token: string;
      };
    }
  | {
      type: 'SET_AGENT_STREAM_ON_STATE_CHANGE';
      payload: AgentState;
    }
  | {
      type: 'SET_AGENT_STREAM_ON_COMPLETE';
      payload: {
        isComplete: boolean;
      };
    }
  | {
      type: 'SET_AGENT_ON_COMPLETE_MESSAGE_RECEIVED';
      payload: {
        message: LLMMessageDTO;
      };
    }
  | { type: 'SET_AGENT_STREAM_STOPPED' }
  | { type: 'SET_AGENT_IS_OBJECTIVE_READY'; payload: boolean } //TODO IS THIS NEEDED?
  | { type: 'SET_AGENT_PROMPT_VARIABLES'; payload: PromptVariables | null }
  | { type: 'SET_AGENT_KEEP_EDITING' }
  | { type: 'SET_COLOR_PALETTE_PICKER'; payload: { presentationId: string } }
  | { type: 'SET_AGENT_CONNECTED'; payload: boolean }
  | { type: 'SET_AGENT_THREAD_ID'; payload: string };

function initDynamicLessonState({ router }: { router?: NextRouter }): DynamicLessonState {
  return getInitialDynamicLessonState(router?.query);
}

const useCreateLessonState = () => {
  const router = useRouter();

  const syncToUrlMiddleware = useCallback(
    (action: DynamicLessonAction, state: DynamicLessonState, router: NextRouter) => {
      //if we dont sync the property to the url there is no need to run this
      const doNotSyncToUrl = [
        'SET_UPLOADED_FILE',
        'SET_SELECTED_STANDARD',
        'CLEAR_SELECTED_STANDARD',
        'SET_GRADE',
        'SET_LANGUAGE',
      ];

      if (doNotSyncToUrl.includes(action.type)) return;

      router.push({
        pathname: router.pathname,
        query: {
          tenantId: router.query.tenantId,
          ...dynamicLessonStateToRouterQuery(state),
        },
      });
    },
    [],
  );

  const [dynamicLessonState, dispatch] = useReducerWithMiddlewareDynamicLesson(
    DynamicLessonReducer,
    initDynamicLessonState({ router }),
    [],
    [syncToUrlMiddleware],
  );

  const dispatchAndSync = useCallback(
    (action: DynamicLessonAction) => {
      dispatch(action);
    },
    [dispatch],
  );

  return {
    dispatchAndSync,
    dynamicLessonState,
  };
};

export const DynamicLessonReducer = (
  state: DynamicLessonState,
  action: DynamicLessonAction,
): DynamicLessonState => {
  switch (action.type) {
    case 'RESET_ALL':
      return {
        ...state,
        prompt: undefined,
        additionalCriteria: undefined,
        grade: state.grade,
        subject: undefined,
        selectedStandard: undefined,
        uploadedFile: undefined,
        mainLessonCategory: undefined,
        languages: state.languages,
        generatorFilterCategory: undefined,
        lessonIntent: undefined,
        selectedPage: Page.SubjectSelection,
        needSupportWith: undefined,
        subjectFocus: undefined,
        targetSkill: undefined,
        addedOwnTargetSkill: undefined,
        lessonTheme: undefined,
        chapter: undefined,
        novel: undefined,
        agent: {
          agentHistory: [],
          currentContent: '',
          isComplete: false,
          isObjectiveReady: false,
          llmThreadId: null,
          promptVariables: null,
          tokens: [],
          isStreaming: false,
          state: null,
          finishReason: null,
          error: null,
          isConnected: false,
        },
      };
    case 'RESET_SUBJECTS':
      return {
        ...state,
        subject: undefined,
        selectedStandard: undefined,
        prompt: undefined,
        additionalCriteria: undefined,
        uploadedFile: undefined,
        selectedPage: Page.SubjectSelection,
        generatorFilterCategory: undefined,
      };
    case 'RESET_LESSON_INTENT':
      return {
        ...state,
        lessonIntent: undefined,
        selectedPage: Page.LessonIntentSelection,
        prompt: undefined,
        selectedStandard: undefined,
        uploadedFile: undefined,
        needSupportWith: undefined,
        subjectFocus: undefined,
        targetSkill: undefined,
        addedOwnTargetSkill: undefined,
        lessonTheme: undefined,
        chapter: undefined,
        generatorFilterCategory: undefined,
      };
    case 'GO_BACK':
      return {
        ...state,
        selectedPage: action.payload,
        prompt: undefined,
        selectedStandard: undefined,
        uploadedFile: undefined,
      };
    case 'SET_PAGE':
      return {
        ...state,
        prompt: undefined,
        selectedStandard: undefined,
        selectedPage: action.payload,
      };
    case 'SET_MAIN_LESSON_CATEGORY':
      if (action.payload === 'test_prep') {
        return {
          ...state,
          mainLessonCategory: action.payload,
          generatorFilterCategory: 'test_prep_made_fun',
        };
      }
      if (action.payload === 'writing_skills') {
        return {
          ...state,
          mainLessonCategory: action.payload,
          generatorFilterCategory: 'five_min_writing',
        };
      }

      return {
        ...state,
        mainLessonCategory: action.payload,
      };
    case 'SET_SUBJECT':
      return {
        ...state,
        subject: action.payload.subject,
        selectedPage: Page.LessonIntentSelection,
      };
    case 'SET_GENERATOR_FILTER_CATEGORY': //used for writing skills and daily routines only
      return {
        ...state,
        generatorFilterCategory: action.payload,
      };
    case 'SET_LESSON_INTENT':
      return {
        ...state,
        lessonIntent: action.payload,
        selectedPage: Page.LessonGoals,
        ...(action.isDesktop &&
          action.payload.id === 'quickWrites' && {
            generatorFilterCategory: 'five_min_writing',
          }),
      };
    case 'SET_UPLOADED_FILE':
      return {
        ...state,
        uploadedFile: action.payload,
      };
    case 'SET_GRADE':
      return {
        ...state,

        grade: action.payload,
      };
    case 'SET_ADVANCED_PROMPT':
      return {
        ...state,
        selectedPage: Page.LessonIntentSelection,
        subject: 'advancedPrompting',
      };
    case 'SET_LANGUAGE':
      return {
        ...state,
        languages: [action.payload],
      };
    case 'SET_TARGET_SKILL':
      return {
        ...state,
        targetSkill: action.payload,
        addedOwnTargetSkill: undefined,
      };
    case 'SET_SUBJECT_FOCUS':
      return {
        ...state,
        subjectFocus: action.payload,
      };
    case 'SET_ADDED_OWN_TARGET_SKILL':
      return {
        ...state,
        addedOwnTargetSkill: action.payload,
      };
    case 'SET_NOVEL':
      return {
        ...state,
        novel: action.payload,
      };
    case 'SET_CHAPTER':
      return {
        ...state,
        chapter: action.payload,
      };

    case 'SET_NEEDS_SUPPORT':
      return {
        ...state,
        needSupportWith: action.payload,
      };
    case 'SET_LESSON_THEME':
      return {
        ...state,
        lessonTheme: action.payload,
      };
    case 'SET_ADDITIONAL_CRITERIA':
      return {
        ...state,
        additionalCriteria: action.payload,
      };
    case 'SET_SELECTED_STANDARD':
      return {
        ...state,
        selectedStandard: action.payload.selectedStandard,
        grade: action.payload.grade,
      };
    case 'CLEAR_SELECTED_STANDARD':
      return {
        ...state,
        selectedStandard: undefined,
      };
    case 'ON_PROMPT_CHANGE':
      return {
        ...state,
        selectedPage: Page.Generate,
        prompt: action.payload,
      };
    case 'HANDLE_RECENT_PROMPT_SELECTED':
      const recentPrompt = action.payload;
      return {
        ...state,
        ...recentPrompt.dynamicLessonState,
      };

    case 'SET_COLOR_PALETTE_PICKER':
      return {
        ...state,
        mainLessonCategory: 'pick_theme',
        presentationId: action.payload.presentationId,
      };

    case 'SET_AGENT_HISTORY':
      return {
        ...state,
        agent: {
          ...state.agent,
          agentHistory: action.payload,
          // not my fav way to do this, but SET_AGENT_HISTORY is only called
          // when we're starting a stream and adding a user message
          isStreaming: true,
        },
      };

    case 'SET_AGENT_HISTORY_FROM_SERVER':
      // TODO: we're having to account for tool calls
      // in a lot of places. We should omit them from the server response
      // to simplify our FE code
      const hasNewMessage =
        action.payload.filter(message => !isToolCall(message)).length >
        state.agent.agentHistory.filter(message => !isToolCall(message)).length;
      return {
        ...state,
        agent: {
          ...state.agent,
          agentHistory: action.payload,
          // Clear streaming content when we get fresh history from server
          // This prevents duplicates when the page reloads
          currentContent: '',
          isStreaming: state.agent.isStreaming && !hasNewMessage,
          tokens: [],
        },
      };

    case 'SET_AGENT_STREAM_STARTED':
      return {
        ...state,
        agent: {
          ...state.agent,
          isStreaming: true,
        },
      };

    case 'RESET_AGENT':
      return {
        ...state,
        agent: {
          ...state.agent,
          isStreaming: false,
          tokens: [],
          isComplete: false,
          currentContent: '',
          state: null,
          error: null,
        },
      };

    case 'SET_AGENT_STREAM_ERROR':
      return {
        ...state,
        agent: {
          ...state.agent,
          error: action.payload,
          isStreaming: false,
          state: null,
          currentContent: '',
          tokens: [],
        },
      };

    case 'CLEAR_AGENT_ERROR':
      return {
        ...state,
        agent: {
          ...state.agent,
          error: null,
        },
      };

    case 'SET_AGENT_CONNECTED':
      return {
        ...state,
        agent: {
          ...state.agent,
          isConnected: action.payload,
        },
      };

    case 'SET_AGENT_STREAM_ON_TOKEN_RECEIVED':
      return {
        ...state,
        agent: {
          ...state.agent,
          tokens: [...state.agent.tokens, action.payload.token],
          currentContent: state.agent.currentContent + action.payload.token,
        },
      };

    case 'SET_AGENT_STREAM_ON_STATE_CHANGE':
      return {
        ...state,
        agent: {
          ...state.agent,
          // this is an assumption that we should make more explicit
          ...(action.payload.state && { isStreaming: true }),
          state: action.payload.state,
          finishReason: action.payload.finishReason,
          isObjectiveReady: action.payload.isObjectiveReady,
          promptVariables: action.payload.promptVariables,
        },
      };

    case 'SET_AGENT_STREAM_ON_COMPLETE':
      return {
        ...state,
        agent: {
          ...state.agent,
          state: null,
          isComplete: action.payload.isComplete,
          isStreaming: false,
          tokens: [],
          // Don't clear currentContent here - let it persist until the message is added to history
          // currentContent: '',
        },
      };

    case 'SET_AGENT_ON_COMPLETE_MESSAGE_RECEIVED':
      return {
        ...state,
        agent: {
          ...state.agent,
          agentHistory: [...state.agent.agentHistory, action.payload.message],
          isComplete: false,
          isStreaming: false, // Don't set back to streaming when message is received
          tokens: [],
          currentContent: '', // Now clear currentContent since message is in history
        },
      };

    case 'SET_AGENT_STREAM_STOPPED':
      return {
        ...state,
        agent: {
          ...state.agent,
          currentContent: '',
          error: null,
          isComplete: false,
          isStreaming: false,
          state: null,
          tokens: [],
        },
      };

    case 'SET_AGENT_THREAD_ID':
      return {
        ...state,
        agent: {
          ...state.agent,
          llmThreadId: action.payload,
        },
      };

    case 'SET_AGENT_KEEP_EDITING':
      return {
        ...state,
        agent: {
          ...state.agent,
          isComplete: false,
          isObjectiveReady: false,
        },
      };

    case 'SET_AGENT_IS_OBJECTIVE_READY':
      return {
        ...state,
        agent: { ...state.agent, isObjectiveReady: action.payload },
      };

    case 'SET_AGENT_PROMPT_VARIABLES':
      return {
        ...state,
        agent: { ...state.agent, promptVariables: action.payload },
      };

    default:
      return state;
  }
};

const decodeQuery = (value?: string) => {
  return value ? decodeURIComponent(value) : undefined;
};

const getInitialDynamicLessonState = (
  routerQuery?: ParsedUrlQuery,
  hasNewBlockAccess?: boolean,
): DynamicLessonState => {
  /*
  Here we are getting the initial state from the router query. 
  We are also getting the grade, language and novel from the local storage if it exists.
  */
  const localState = typeof window !== 'undefined' ? loadDynamicLessonState() : null;
  const subject = routerQuery?.subject as string;
  const generatorFilterCategory = routerQuery?.category as string;
  const mainLessonCategory = routerQuery?.lessonCategory as
    | 'full_lesson'
    | 'test_prep'
    | 'writing_skills'
    | 'upload'
    | 'pick_theme';
  const lessonIntentId = routerQuery?.lessonIntentId as string;
  const selectedPage = Number(routerQuery?.sp);
  const additionalCriteria = decodeQuery(routerQuery?.additionalCriteria as string);
  const prompt = decodeQuery(routerQuery?.prompt as string);
  const needSupportWith = decodeQuery(routerQuery?.needSupportWith as string);
  const subjectFocus = decodeQuery(routerQuery?.subjectFocus as string);
  const targetSkill = decodeQuery(routerQuery?.targetSkill as string);
  const addedOwnTargetSkill = decodeQuery(routerQuery?.addedOwnTargetSkill as string);
  const lessonTheme = decodeQuery(routerQuery?.lessonTheme as string);
  const chapter = decodeQuery(routerQuery?.chapter as string);
  const novel = decodeQuery(routerQuery?.novel as string);

  const languages = [];

  const localLanguage = localState?.languages || undefined;

  const initialLanguage: string[] = localLanguage ? localLanguage : ['english'];
  if (initialLanguage && initialLanguage.length > 0) {
    languages.push(initialLanguage[0]);
  }

  if (languages.length === 0) {
    languages.push('english');
  }

  const initialNovel = novel || localState?.novel || undefined;
  const initialGrade = localState?.grade || '6th';

  return {
    generatorFilterCategory: generatorFilterCategory,
    mainLessonCategory: mainLessonCategory,
    lessonIntent:
      lessonIntentId && subject
        ? findlessonIntent({
            subject: subject,
            lessonIntentId,
            hasNewBlockAccess: !!hasNewBlockAccess,
          })
        : undefined,
    selectedPage: selectedPage || Page.SubjectSelection,
    languages,
    prompt,
    grade: initialGrade,
    subject,
    selectedStandard: undefined,
    uploadedFile: undefined,
    targetSkill,
    addedOwnTargetSkill,
    lessonTheme,
    subjectFocus,
    needSupportWith,
    novel: initialNovel,
    chapter,
    additionalCriteria,
    agent: {
      agentHistory: [],
      currentContent: '',
      isComplete: false,
      isObjectiveReady: false,
      llmThreadId: null,
      promptVariables: null,
      tokens: [],
      isStreaming: false,
      state: null,
      finishReason: null,
      error: null,
      isConnected: false,
    },
  };
};

const dynamicLessonStateToRouterQuery = (state: DynamicLessonState) => {
  const lessonQueryParameters: Record<string, string | number> = {};
  if (state.subject) lessonQueryParameters.subject = state.subject;
  if (state.generatorFilterCategory)
    lessonQueryParameters.category = state.generatorFilterCategory;
  if (state.mainLessonCategory)
    lessonQueryParameters.lessonCategory = state.mainLessonCategory;
  if (state.lessonIntent) lessonQueryParameters.lessonIntentId = state.lessonIntent?.id;
  if (state.selectedPage > 1) lessonQueryParameters.sp = state.selectedPage;

  // Encoded inputs
  const encodedInputs: Record<string, string | undefined> = {
    targetSkill: state.targetSkill,
    addedOwnTargetSkill: state.addedOwnTargetSkill,
    lessonTheme: state.lessonTheme,
    novel:
      state.lessonIntent?.id === 'novelStudy' || state.lessonIntent?.id === 'readAloud'
        ? state.novel
        : undefined,
    chapter: state.chapter,
    subjectFocus: state.subjectFocus,
    needSupportWith: state.needSupportWith,
    additionalCriteria: state.additionalCriteria,
  };

  Object.entries(encodedInputs).forEach(([key, value]) => {
    if (value) {
      lessonQueryParameters[key] = encodeURIComponent(value);
    }
  });

  // truncate very long prompts
  if (state.prompt) {
    const truncatedPrompt =
      state.prompt.length > 1000 ? state.prompt.slice(0, 1000) : state.prompt;
    const encodedPrompt = encodeURIComponent(truncatedPrompt);
    lessonQueryParameters.prompt = encodedPrompt;
  }

  return lessonQueryParameters;
};

type DynamicLessonStateContextType = {
  dynamicLessonState: DynamicLessonState;
  dispatchAndSync: Dispatch<DynamicLessonAction>;
};

export const DynamicLessonStateContext = createContext<DynamicLessonStateContextType>({
  dynamicLessonState: initDynamicLessonState({}),
  dispatchAndSync: (_action: DynamicLessonAction) => undefined,
});

export function useDynamicLessonStateContext<T>(
  selector: ContextSelector<DynamicLessonStateContextType, T>,
): T {
  return useContextSelector(DynamicLessonStateContext, selector);
}

export const DynamicLessonStateProvider = DynamicLessonStateContext.Provider;

export default useCreateLessonState;
